"use client";

import { FC, useState } from "react";

import { useStyles } from "./styles";
import { Nullable } from "@/types/common";
import { AlertDialogType } from "@/types/alert";
import { useAppSelector, useAppDispatch } from "@/redux/store";
import {
  setActiveComment,
  editComment,
  createComment,
  addAlertDialog,
} from "@/redux/actions";
import Comment from "../Comment";
import CommentForm from "../CommentForm";

interface CommentsProps {
  currentUserId: Nullable<number>;
  roomUUID?: string | null;
  articleSlug?: string | null;
  questionSlug?: string | null;
  quizSlug?: string | null;
  imageId?: number | null;
  className?: string;
}

const Comments: FC<CommentsProps> = ({
  currentUserId,
  roomUUID = null,
  articleSlug = null,
  questionSlug = null,
  quizSlug = null,
  imageId = null,
  className = "",
}) => {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const activeComment = useAppSelector((state) => state.comments.activeComment);
  const backendComments = useAppSelector((state) => state.comments.comments);

  const rootComments = backendComments.filter(
    (backendComment) => backendComment.parentId === null
  );

  const getReplies = (commentId: number) => {
    return backendComments
      .filter((backendComment) => backendComment.parentId === commentId)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
  };

  const addComment = (text: string, parentId: Nullable<number>) => {
    dispatch(
      createComment({
        parentId,
        roomUUID,
        articleSlug,
        questionSlug,
        quizSlug,
        imageId,
        text,
      })
    );
    dispatch(setActiveComment(null));
  };

  const updateComment = (text: string, commentId: number) => {
    dispatch(
      editComment({
        commentId,
        roomUUID,
        articleSlug,
        questionSlug,
        quizSlug,
        imageId,
        text,
      })
    );
    dispatch(setActiveComment(null));
  };

  const removeComment = (commentId: number) => {
    dispatch(
      addAlertDialog({
        title: "Delete comment",
        description:
          "Are you sure you want to delete this comment? This action cannot be undone.",
        nextButtonText: "Confirm Delete",
        type: AlertDialogType.DeleteComment,
        data: {
          commentId,
          roomUUID,
          articleSlug,
          questionSlug,
          quizSlug,
          imageId,
        },
      })
    );
  };

  return (
    <div className={className}>
      <CommentForm handleSubmit={addComment} currentUserId={currentUserId} />
      <div className={classes.rootComments}>
        {rootComments.map((rootComment) => (
          <Comment
            key={rootComment.id}
            comment={rootComment}
            replies={getReplies(rootComment.id)}
            currentUserId={currentUserId}
            removeComment={removeComment}
            activeComment={activeComment}
            addComment={addComment}
            updateComment={updateComment}
            setActiveComment={setActiveComment}
            parentId={null}
          />
        ))}
      </div>
    </div>
  );
};

export default Comments;
